import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchProductSuggestions, fetchSalonProducts, updateProductStock } from '../../api/products';
import { fetchBranches } from '../../api/salon';
import DashboardShell from '../../components/ui/DashboardShell';
import PaginatedFooter from '../../components/ui/PaginatedFooter';
import { DEFAULT_PAGE_SIZE } from '../../constants/pagination';
import ListSearchToolbar from '../../components/ui/ListSearchToolbar';
import { useListSearch } from '../../hooks/useListSearch';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';

const SalonInventoryPage = () => {
  const { t } = useTranslation();
  const { tenantId } = useAuth();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [branchId, setBranchId] = useState('');
  const [lowOnly, setLowOnly] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const {
    input: searchInput,
    setInput: setSearchInput,
    appliedSearch,
    commitSearch,
    suggestions,
    suggestionsLoading,
  } = useListSearch({
    queryKeyPrefix: ['inventory-suggestions', tenantId, branchId, lowOnly],
    fetchSuggestions: (q) =>
      fetchProductSuggestions({
        q,
        branchId: branchId || undefined,
      }),
    enabled: !!tenantId,
  });

  const stockStatus = (p) => {
    if (p.stock <= 0) return { label: t('salon.inventory.statusOut'), className: 'bg-red-900/40 text-red-300' };
    if (p.stock <= p.lowStockThreshold) return { label: t('salon.inventory.statusLow'), className: 'bg-amber-900/40 text-amber-300' };
    return { label: t('salon.inventory.statusOk'), className: 'bg-emerald-900/40 text-emerald-300' };
  };

  const { data: branchData } = useQuery({
    queryKey: ['branches'],
    queryFn: fetchBranches,
  });

  const { data, isLoading } = useQuery({
    queryKey: ['salon-products', 'inventory', tenantId, branchId, lowOnly, page, pageSize, appliedSearch],
    queryFn: () =>
      fetchSalonProducts({
        branchId: branchId || undefined,
        lowStock: lowOnly ? 'true' : undefined,
        search: appliedSearch || undefined,
        page,
        limit: pageSize,
      }),
    enabled: !!tenantId,
  });

  const stockMut = useMutation({
    mutationFn: ({ id, stock }) => updateProductStock(id, stock),
    onSuccess: () => {
      showToast(t('toast.stockUpdated'), 'success');
      setEditingId(null);
      queryClient.invalidateQueries({ queryKey: ['salon-products'] });
    },
    onError: (err) => showToast(err.response?.data?.message || t('toast.updateFailed'), 'error'),
  });

  const products = data?.products || [];
  const pagination = data?.pagination;

  const lowAlerts = products.filter((p) => p.stock > 0 && p.stock <= p.lowStockThreshold);
  const branches = branchData?.branches || [];
  const branchName = (id) => branches.find((b) => b._id === id)?.name || t('common.allSalons');

  if (!tenantId) {
    return (
      <DashboardShell title={t('salon.inventory.title')} subtitle={t('salon.inventory.subtitle')}>
        <p className="text-zinc-600 dark:text-zinc-500">{t('salon.inventory.tenantRequired')}</p>
      </DashboardShell>
    );
  }

  return (
    <DashboardShell title={t('salon.inventory.title')} subtitle={t('salon.inventory.subtitle')}>
      {lowAlerts.length > 0 && (
        <div className="mb-6 rounded-xl border border-amber-800/50 bg-amber-950/30 p-4">
          <h3 className="font-medium text-amber-300">{t('salon.inventory.lowStockAlerts')}</h3>
          <ul className="mt-2 space-y-1 text-sm text-amber-200/80">
            {lowAlerts.map((p) => (
              <li key={p._id}>
                {p.name} — {t('salon.inventory.stockLeft', { count: p.stock, threshold: p.lowStockThreshold })}
              </li>
            ))}
          </ul>
        </div>
      )}

      <ListSearchToolbar
        searchInput={searchInput}
        onSearchInputChange={setSearchInput}
        onSearch={(v) => { commitSearch(v); setPage(1); }}
        suggestions={suggestions}
        suggestionsLoading={suggestionsLoading}
        searchPlaceholder={t('salon.products.searchPlaceholder')}
        filtersOpen={filtersOpen}
        onToggleFilters={() => setFiltersOpen((o) => !o)}
      >
        <select value={branchId} onChange={(e) => { setBranchId(e.target.value); setPage(1); }} className="rounded-lg border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-950 px-3 py-2 text-sm">
          <option value="">{t('appointments.filters.allBranches')}</option>
          {branches.map((b) => (
            <option key={b._id} value={b._id}>{b.name}</option>
          ))}
        </select>
        <label className="flex items-center gap-2 text-sm text-zinc-600 dark:text-zinc-400">
          <input type="checkbox" checked={lowOnly} onChange={(e) => { setLowOnly(e.target.checked); setPage(1); }} />
          {t('salon.inventory.lowStockOnly')}
        </label>
      </ListSearchToolbar>

      {isLoading && <p className="text-zinc-600 dark:text-zinc-500">{t('common.loading')}</p>}

      <div className="overflow-x-auto rounded-xl border border-zinc-200 dark:border-zinc-800">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900/80 text-zinc-600 dark:text-zinc-500">
            <tr>
              <th className="p-3">{t('salon.inventory.product')}</th>
              <th className="p-3">{t('common.branch')}</th>
              <th className="p-3">{t('common.category')}</th>
              <th className="p-3">{t('salon.inventory.stock')}</th>
              <th className="p-3">{t('salon.inventory.threshold')}</th>
              <th className="p-3">{t('salon.inventory.status')}</th>
            </tr>
          </thead>
          <tbody>
            {products.map((p) => {
              const st = stockStatus(p);
              return (
                <tr key={p._id} className="border-b border-zinc-200/50 dark:border-zinc-800/50">
                  <td className="p-3 font-medium">{p.name}</td>
                  <td className="p-3 text-zinc-600 dark:text-zinc-500">{p.branchId ? branchName(p.branchId) : t('salon.inventory.allBranchesLabel')}</td>
                  <td className="p-3 text-zinc-600 dark:text-zinc-500">{p.category}</td>
                  <td className="p-3">
                    {editingId === p._id ? (
                      <form
                        className="flex gap-2"
                        onSubmit={(e) => {
                          e.preventDefault();
                          stockMut.mutate({ id: p._id, stock: Number(editValue) });
                        }}
                      >
                        <input
                          type="number"
                          min={0}
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          className="w-20 rounded border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-950 px-2 py-1"
                          autoFocus
                        />
                        <button type="submit" className="text-xs text-glow-400">{t('common.save')}</button>
                        <button type="button" className="text-xs text-zinc-600 dark:text-zinc-500" onClick={() => setEditingId(null)}>{t('common.cancel')}</button>
                      </form>
                    ) : (
                      <button
                        type="button"
                        className="hover:text-glow-400"
                        onClick={() => {
                          setEditingId(p._id);
                          setEditValue(String(p.stock));
                        }}
                      >
                        {p.stock}
                      </button>
                    )}
                  </td>
                  <td className="p-3 text-zinc-600 dark:text-zinc-500">{p.lowStockThreshold}</td>
                  <td className="p-3">
                    <span className={`rounded-full px-2 py-0.5 text-xs ${st.className}`}>{st.label}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <PaginatedFooter
          pagination={pagination}
          page={page}
          onPageChange={setPage}
          pageSize={pageSize}
          onPageSizeChange={(n) => { setPageSize(n); setPage(1); }}
        />
      </div>
    </DashboardShell>
  );
};

export default SalonInventoryPage;
